// The app's form control kit: the labelled section, the text field, the
// select and the checkbox that every long form on the panel is built out of.
//
// These started life inside the brand feature and moved here once the client
// form needed the same controls — a second copy of a text field is how two
// screens quietly stop looking alike.
//
// Upload controls are not here: they carry their own accepted-type and size
// rules, so they live next to the feature that sets them.

import React from "react";
import CustomDropdown from "./CustomDropdown";
import type { DropdownFooterAction, DropdownOption } from "./CustomDropdown";
import DatePicker from "./DatePicker";
import MultiSelect from "./MultiSelect";

/** One labelled group of fields: caption above, white card below. */
export const FormSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <section>
    {/* Row plus margin comes to 40px, matching the brand preview panel's
        document switcher opposite it, so on that screen both columns' cards
        start on the same line. */}
    <h2 className="mb-2 flex h-8 items-end text-[13px] 3xl:text-[14px] text-ink-muted">
      {title}
    </h2>
    <div className="rounded-md border border-line bg-white p-4 sm:p-5 3xl:p-6">
      {children}
    </div>
  </section>
);

/** Exported for the upload controls, which are labelled the same way. */
export const fieldLabelClasses = "block text-[13px] 3xl:text-[14px] text-ink";

// Size and shape, with no colour of their own: shared by the bare input and by
// the box drawn around a prefixed one, so the two sit at the same height and
// line up wherever a form puts them in the same row.
const controlShellClasses =
  "w-full rounded-[6px] border px-3.5 py-2.5 3xl:py-3 " +
  "text-[14px] 3xl:text-[15px] transition-colors";

const controlClasses = (invalid: boolean) =>
  `${controlShellClasses} bg-white text-ink placeholder:text-ink-subtle ` +
  "focus:outline-none " +
  "disabled:cursor-not-allowed disabled:bg-[#F7F9FA] disabled:text-ink-muted " +
  (invalid
    ? "border-red-400 focus:border-red-500"
    : "border-line focus:border-brand");

/**
 * The same box, but drawn around a prefix and the input together. The border
 * belongs to the box, so `focus-within` is what lights the control — a caret
 * in the input has to look the same as focus on a plain field does.
 *
 * Disabled is spelled out rather than left to `disabled:`, because the wrapper
 * is a `div` and that variant only ever matches the input inside it.
 */
const prefixedControlClasses = (invalid: boolean, disabled: boolean) =>
  `${controlShellClasses} flex items-center gap-1.5 ` +
  "focus-within:outline-none " +
  (disabled ? "cursor-not-allowed bg-[#F7F9FA] text-ink-muted " : "bg-white text-ink ") +
  (invalid
    ? "border-red-400 focus-within:border-red-500"
    : "border-line focus-within:border-brand");

/**
 * The same box again, for a feature control that draws its own insides — the
 * settings screen's experience stepper, which holds an input, a unit and a
 * pair of arrows. Exported for the reason `fieldLabelClasses` is: a control
 * built outside this file still has to sit at the height every other row does,
 * and a second copy of the border and padding is how two forms quietly stop
 * looking alike.
 */
export const controlBoxClasses = (invalid = false, disabled = false): string =>
  prefixedControlClasses(invalid, disabled);

// Inside the box above the input is only the caret and the text: the frame it
// would otherwise draw is the wrapper's job now.
const prefixedInputClasses =
  "min-w-0 flex-1 bg-transparent text-inherit placeholder:text-ink-subtle " +
  "focus:outline-none disabled:cursor-not-allowed";

/**
 * Label plus, for required fields, a red asterisk. The asterisk sits outside
 * the `<label>` on purpose: it is decorative, and inside it would become part
 * of the control's accessible name.
 */
const FieldLabel: React.FC<{
  htmlFor: string;
  children: React.ReactNode;
  required?: boolean;
  /** Set when the control is named by `aria-labelledby` rather than `for`. */
  labelId?: string;
  /**
   * Drawn after the label — the count of answers a multi-select is holding.
   * Outside the `<label>`, like the asterisk and for the same reason: it is
   * the field's state, not its name, and the control says it for itself.
   */
  suffix?: React.ReactNode;
}> = ({ htmlFor, children, required, labelId, suffix }) => (
  <div className="mb-1.5 flex items-center gap-1">
    <label id={labelId} htmlFor={htmlFor} className={fieldLabelClasses}>
      {children}
    </label>
    {required && (
      <span aria-hidden="true" className="text-red-500 leading-none">
        *
      </span>
    )}
    {suffix}
  </div>
);

const FieldError: React.FC<{ id: string; message?: string }> = ({
  id,
  message,
}) =>
  message ? (
    <p id={id} className="mt-1.5 text-[11px] 3xl:text-[12px] text-red-500">
      {message}
    </p>
  ) : null;

interface TextFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  label?: string;
  onValueChange: (value: string) => void;
  /** Shown under the control once the field has been visited. */
  error?: string;
  /**
   * The field's standing rule — a length ceiling, an accepted format — set
   * under the control the way the upload fields set theirs. An error takes its
   * place while there is one: the two say the same thing, and only one of them
   * is news.
   */
  hint?: string;
  required?: boolean;
  /**
   * Fixed text painted inside the control, ahead of the value — the `+91` on a
   * phone field. It is not part of the value: nothing typed can reach it, and
   * `onValueChange` never sees it. It is announced as the field's description
   * so the number is still read out with its dialling code.
   */
  prefix?: string;
}

export const TextField: React.FC<TextFieldProps> = ({
  label,
  value,
  onValueChange,
  className = "",
  id,
  placeholder,
  error,
  hint,
  required,
  prefix,
  disabled,
  ...rest
}) => {
  // Falls back to the placeholder so every control is still reachable by an
  // accessible name on rows a design leaves unlabelled (brand bank details).
  const generatedId = React.useId();
  const fieldId = id ?? generatedId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  const prefixId = `${fieldId}-prefix`;
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Whichever line is actually under the control is the one announced with it.
  const describedBy = [
    prefix ? prefixId : "",
    error ? errorId : hint ? hintId : "",
  ]
    .filter(Boolean)
    .join(" ");

  const input = (
    <input
      ref={inputRef}
      id={fieldId}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      aria-label={label ? undefined : placeholder}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy || undefined}
      onChange={(event) => onValueChange(event.target.value)}
      className={prefix ? prefixedInputClasses : controlClasses(!!error)}
      {...rest}
    />
  );

  return (
    <div className={className}>
      {label && (
        <FieldLabel htmlFor={fieldId} required={required}>
          {label}
        </FieldLabel>
      )}
      {prefix ? (
        <div
          className={prefixedControlClasses(!!error, !!disabled)}
          // The prefix is painted inside the control, so clicking it has to
          // behave like clicking the control. It takes no pointer events of its
          // own, which leaves the box itself as the target for those clicks —
          // the input's own stay with the input, caret placement intact.
          onMouseDown={(event) => {
            if (event.target !== event.currentTarget) return;
            event.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <span
            id={prefixId}
            className="pointer-events-none select-none text-ink-muted"
          >
            {prefix}
          </span>
          {input}
        </div>
      ) : (
        input
      )}
      {error ? (
        <FieldError id={errorId} message={error} />
      ) : (
        hint && (
          <p
            id={hintId}
            className="mt-1.5 text-right text-[11px] 3xl:text-[12px] text-ink-subtle"
          >
            {hint}
          </p>
        )
      )}
    </div>
  );
};

interface TextAreaFieldProps
  extends Omit<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    "onChange"
  > {
  label?: string;
  onValueChange: (value: string) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  /**
   * Takes the height left over in the column it stands in, rather than
   * standing at whatever `rows` comes to.
   *
   * For a form laid out in two columns, where the box is the only control on
   * its side that can absorb a difference: without it the short column stops
   * where its last field does and leaves a block of air beside a column that
   * runs on, which reads as a form that did not finish loading.
   *
   * `rows` is still the floor. The growing is done with `grow` rather than
   * `flex-1` precisely so that it is: `flex-1` sets the basis to zero, which
   * throws the natural height away and collapses the box in any column that
   * has no space going spare.
   *
   * It only does anything inside a flex column that is itself being stretched
   * — which, below the breakpoint where the two columns stack, it is not. The
   * box falls back to `rows` there, which is what a single column wants.
   */
  grow?: boolean;
}

/**
 * The same field again, for an answer that runs to sentences — the support
 * screen's "Explain your issue".
 *
 * It wears the text field's own box, so a form holding both sits at one width
 * with one border. `resize-y` and not `resize`: the height is the writer's to
 * choose, the width belongs to the column it is in, and a control dragged wider
 * than its card is how a tidy form ends up looking broken.
 */
export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  label,
  value,
  onValueChange,
  className = "",
  id,
  placeholder,
  error,
  hint,
  required,
  rows = 4,
  grow = false,
  ...rest
}) => {
  // Falls back to the placeholder so the control is still reachable by an
  // accessible name on the rows a design leaves unlabelled.
  const generatedId = React.useId();
  const fieldId = id ?? generatedId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;

  return (
    // A flex column only where the box is meant to fill one, so every form
    // already using this field keeps the plain block it was laid out as.
    <div className={`${grow ? "flex grow flex-col" : ""} ${className}`}>
      {label && (
        <FieldLabel htmlFor={fieldId} required={required}>
          {label}
        </FieldLabel>
      )}
      <textarea
        id={fieldId}
        value={value}
        rows={rows}
        placeholder={placeholder}
        aria-label={label ? undefined : placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : hint ? hintId : undefined}
        onChange={(event) => onValueChange(event.target.value)}
        className={`${controlClasses(!!error)} resize-y leading-[1.6] ${
          grow ? "grow" : ""
        }`}
        {...rest}
      />
      {error ? (
        <FieldError id={errorId} message={error} />
      ) : (
        hint && (
          <p
            id={hintId}
            className="mt-1.5 text-right text-[11px] 3xl:text-[12px] text-ink-subtle"
          >
            {hint}
          </p>
        )
      )}
    </div>
  );
};

interface DateFieldProps {
  label: string;
  /** Set where something outside has to reach the input — see `controlId`. */
  id?: string;
  /** `DD/MM/YYYY`, or empty. */
  value: string;
  onValueChange: (value: string) => void;
  /** Runs typed text through the form's normaliser before it lands. */
  normalise?: (raw: string) => string;
  placeholder?: string;
  /** Marks the field visited — fires when the calendar closes, however it did. */
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  /** Days outside these are shown but cannot be picked. */
  minDate?: Date;
  maxDate?: Date;
  className?: string;
}

/**
 * A date field, labelled the same way every other control here is: the label
 * and error wrapped around `DatePicker`, which is what holds the calendar.
 */
export const DateField: React.FC<DateFieldProps> = ({
  label,
  id,
  value,
  onValueChange,
  normalise,
  placeholder,
  onBlur,
  error,
  required,
  disabled,
  minDate,
  maxDate,
  className = "",
}) => {
  const generatedId = React.useId();
  const fieldId = id ?? generatedId;
  const labelId = `${fieldId}-label`;
  const errorId = `${fieldId}-error`;

  return (
    <div className={className}>
      <FieldLabel htmlFor={fieldId} labelId={labelId} required={required}>
        {label}
      </FieldLabel>
      <DatePicker
        id={fieldId}
        value={value}
        onValueChange={onValueChange}
        normalise={normalise}
        placeholder={placeholder}
        onBlur={onBlur}
        invalid={!!error}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        aria-labelledby={labelId}
        aria-describedby={error ? errorId : undefined}
      />
      <FieldError id={errorId} message={error} />
    </div>
  );
};

interface MultiSelectFieldProps {
  label: string;
  /** Set where something outside has to reach the trigger — see `controlId`. */
  id?: string;
  values: string[];
  placeholder: string;
  /**
   * The list to pick from. Plain strings where the value is the label — which
   * is nearly everywhere — and whole options where they differ, the way
   * `DropdownField` takes them: the plan form's Support channels field
   * displays "Whatsapp" and holds the backend's `WHATSAPP`.
   */
  options: ReadonlyArray<string | DropdownOption>;
  onValuesChange: (values: string[]) => void;
  /** Marks the field visited — fires when the list closes, however it closed. */
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  /** Given a reason, a disabled field stays reachable so it can say it. */
  onDisabledClick?: () => void;
  className?: string;
}

/**
 * The select for fields that take more than one answer: the same label and
 * error wrapper as `DropdownField`, around `MultiSelect` instead.
 */
export const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  label,
  id,
  values,
  placeholder,
  options,
  onValuesChange,
  onBlur,
  error,
  required,
  disabled,
  onDisabledClick,
  className = "",
}) => {
  const generatedId = React.useId();
  const fieldId = id ?? generatedId;
  const labelId = `${fieldId}-label`;
  const errorId = `${fieldId}-error`;

  const items = React.useMemo(
    () =>
      options.map((option) =>
        typeof option === "string" ? { value: option, label: option } : option
      ),
    [options]
  );

  return (
    <div className={className}>
      <FieldLabel
        htmlFor={fieldId}
        labelId={labelId}
        required={required}
        // How many have been picked, where the row below can only show the
        // ones that fit. `aria-hidden` because the control reads the same
        // count out for itself — announcing it twice is noise.
        suffix={
          values.length > 0 ? (
            <span
              aria-hidden="true"
              className="text-[13px] 3xl:text-[14px] text-ink-muted"
            >
              ({values.length})
            </span>
          ) : undefined
        }
      >
        {label}
      </FieldLabel>
      <MultiSelect
        id={fieldId}
        values={values}
        placeholder={placeholder}
        options={items}
        onChange={onValuesChange}
        onBlur={onBlur}
        disabled={disabled}
        onDisabledClick={onDisabledClick}
        invalid={!!error}
        aria-labelledby={labelId}
        aria-describedby={error ? errorId : undefined}
      />
      <FieldError id={errorId} message={error} />
    </div>
  );
};

interface DropdownFieldProps {
  /** Omit on rows the design leaves unlabelled; name them with `aria-label`. */
  label?: string;
  "aria-label"?: string;
  /** Set where something outside has to reach the trigger — see `controlId`. */
  id?: string;
  value: string;
  placeholder: string;
  /**
   * The list to pick from. Plain strings where the value is the label — which
   * is nearly everywhere — and whole options where they differ: the client
   * form's Plan field displays "Weight loss | 2 months" and holds the
   * plan's `public_id`.
   */
  options: ReadonlyArray<string | DropdownOption>;
  onValueChange: (value: string) => void;
  /** Marks the field visited — fires when the list closes, however it closed. */
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  /** Set where the list has nothing to offer yet — see the brand City field. */
  disabled?: boolean;
  /** Given a reason, a disabled field stays reachable so it can say it. */
  onDisabledClick?: () => void;
  /** Turns the trigger into a filter box, for lists too long to scroll. */
  searchable?: boolean;
  /** Lets a value the list does not hold be used anyway. */
  creatable?: boolean;
  /** A row pinned under the open list — see `DropdownFooterAction`. */
  footerAction?: DropdownFooterAction;
  /**
   * How many rows the open list builds before it waits to be scrolled — see
   * `CustomDropdown`, which defaults it to sixty.
   *
   * Worth raising only for a list that is both short and read by scrolling
   * rather than by typing: every quarter hour of a day is ninety-six rows, and
   * somebody looking for five in the evening should not meet a list that stops
   * at three in the afternoon until they nudge it. A long list — the brand's
   * cities — wants the default, which is what the limit is for.
   */
  optionPageSize?: number;
  /**
   * The row an unanswered list opens looking at — see `CustomDropdown`. It
   * scrolls and nothing else; the field stays empty until somebody picks.
   */
  openAt?: string;
  className?: string;
}

/**
 * The app's one select control: a label and error message wrapped around
 * `CustomDropdown`. It replaced a native `<select>` variant that used to live
 * here — the open list is part of the design, and two select implementations
 * only invited drift between them.
 */
export const DropdownField: React.FC<DropdownFieldProps> = ({
  label,
  "aria-label": ariaLabel,
  id,
  value,
  placeholder,
  options,
  onValueChange,
  onBlur,
  error,
  required,
  disabled,
  onDisabledClick,
  searchable,
  creatable,
  footerAction,
  optionPageSize,
  openAt,
  className = "",
}) => {
  const generatedId = React.useId();
  const fieldId = id ?? generatedId;
  const labelId = `${fieldId}-label`;
  const errorId = `${fieldId}-error`;

  // The brand city list runs past a hundred entries, and a form re-renders on
  // every keystroke anywhere in it — without this, each one rebuilds a list
  // that has not changed.
  const items = React.useMemo(
    () =>
      options.map((option) =>
        typeof option === "string" ? { value: option, label: option } : option
      ),
    [options]
  );

  return (
    <div className={className}>
      {label && (
        <FieldLabel htmlFor={fieldId} labelId={labelId} required={required}>
          {label}
        </FieldLabel>
      )}
      <CustomDropdown
        id={fieldId}
        value={value}
        placeholder={placeholder}
        options={items}
        openAt={openAt}
        onChange={onValueChange}
        onBlur={onBlur}
        disabled={disabled}
        onDisabledClick={onDisabledClick}
        searchable={searchable}
        creatable={creatable}
        footerAction={footerAction}
        optionPageSize={optionPageSize}
        invalid={!!error}
        // A searchable list takes the whole screen on a phone, and a screen
        // that has taken a form's place has to say which question it is
        // asking. The `<label>` above cannot travel, so its words do.
        heading={label ?? ariaLabel}
        // Named by its label where there is one; on the rows the design leaves
        // unlabelled, by the `aria-label` given instead.
        aria-labelledby={label ? labelId : undefined}
        aria-label={label ? undefined : ariaLabel ?? placeholder}
        aria-describedby={error ? errorId : undefined}
      />
      <FieldError id={errorId} message={error} />
    </div>
  );
};

// The checkbox has its own file — it is drawn rather than native, so it is
// more than a wrapper — but it is still part of this kit, and re-exported here
// so a form can pull all of its controls from one import.
export { Checkbox } from "./Checkbox";
export type { CheckboxProps } from "./Checkbox";
